function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useMemo, useState, useEffect } from 'react';
import { fetchQuery } from 'relay-runtime';
import debounce from 'lodash/debounce';
import { Typography } from 'seagull';
import { useIsMounted } from "../../../../../hooks/useIsMounted";
import { useRelayContext, useGlobalContext, useStoreActions } from "../../../../../contexts";
import { logoUploadUrlQuery } from "../../../../../queries/logoUploadUrl";
import { LogoPicker } from "./LogoPicker/LogoPicker";
import { ColorSelector } from "./ColorSelector/ColorSelector";
import { ColorPicker } from "./ColorPicker/ColorPicker";
import { BrandPreview } from "./BrandPreview/BrandPreview";
import { Box } from "../../BusinessOnboardingWidget.utils";
import styles from "./Brand.module.scss";
export const presetColors = ['#333333', '#999999', '#C94A51', '#EECE7E', '#57A492', '#5186EC', '#6251A2'];
const defaultColor = presetColors[0];
export default function Brand(props) {
  const {
    userInfo
  } = props;
  const worksAt = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.worksAt) || {};
  const {
    logoUrl,
    branding
  } = worksAt;
  const brandingColor = branding === null || branding === void 0 ? void 0 : branding.color;
  const {
    environment
  } = useRelayContext();
  const globalContext = useGlobalContext();
  const {
    setWorksAt,
    logBI,
    updateBusinessColor
  } = useStoreActions();
  const isMounted = useIsMounted();
  const [logoImage, setLogoImage] = useState(logoUrl || '');
  const [savingLogo, setSavingLogo] = useState(false);
  const [brandColor, setBrandColor] = useState(brandingColor || defaultColor);
  useEffect(() => {
    setBrandColor(brandingColor || defaultColor);
  }, [brandingColor]);
  const isPresetColorSelected = useMemo(() => {
    return presetColors.includes(brandColor);
  }, [brandColor]);
  const debouncedUpdateColorApi = useMemo(() => {
    return debounce(async color => {
      try {
        await updateBusinessColor(color);
      } catch (err) {
        globalContext.showErrorToast('Error updating brand color');
        isMounted() && setBrandColor(color);
      }
    }, 500);
  }, [environment, globalContext, isMounted, updateBusinessColor]);

  const changeBrandColor = color => {
    logBI(11209);
    setBrandColor(color);
    debouncedUpdateColorApi(color);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.root,
    "data-testid": "onboarding-widget-content-brand"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    className: styles.title,
    variant: "h6",
    weight: "normal"
  }, "Align the look and feel of your agreements with your brand"), /*#__PURE__*/React.createElement(Typography, {
    className: styles.subtitle,
    variant: "paragraph1"
  }, "Your logo and brand color will appear on your agreements.")), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", {
    className: styles.boxesWrapper
  }, /*#__PURE__*/React.createElement(Box, {
    className: styles.logoBox
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.boxTitle,
    variant: "paragraph1",
    noWrap: true
  }, "Upload logo"), /*#__PURE__*/React.createElement(LogoPicker, {
    testid: "onboarding-widget-brand-logo",
    image: logoImage,
    loading: savingLogo,
    onChange: async data => {
      if (!data) return;
      logBI(11207);
      const prevLogoImage = logoImage;
      setLogoImage(data.asDataURL);
      setSavingLogo(true);

      try {
        const res = await fetchQuery(environment, logoUploadUrlQuery, {});
        const url = res.logoUploadUrl.replace(/"/g, '');
        await fetch(url, {
          method: 'PUT',
          body: data.file
        });
        setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
          logoUrl: data.asDataURL
        }));
      } catch (err) {
        globalContext.showErrorToast('Error saving logo');
        isMounted() && setLogoImage(prevLogoImage);
      } finally {
        isMounted() && setSavingLogo(false);
      }
    }
  })), /*#__PURE__*/React.createElement(Box, {
    className: styles.brandBox
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.boxTitle,
    variant: "paragraph1"
  }, "Choose brand color"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles.colorOptions
  }, presetColors.map((color, idx) => /*#__PURE__*/React.createElement(ColorSelector, {
    key: color,
    color: color,
    selected: color === brandColor,
    testid: "onboarding-widget-brand-color-".concat(idx),
    onClick: () => changeBrandColor(color)
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.separator
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.line
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: "secondary"
  }, "Or"), /*#__PURE__*/React.createElement("div", {
    className: styles.line
  })), /*#__PURE__*/React.createElement(ColorPicker, {
    color: brandColor,
    selected: !isPresetColorSelected,
    displayColor: isPresetColorSelected ? undefined : brandColor,
    testid: "onboarding-widget-brand-picker",
    onChange: setBrandColor,
    onBlur: () => changeBrandColor(brandColor)
  })))), /*#__PURE__*/React.createElement("div", {
    className: styles.preview,
    style: {
      color: brandColor
    }
  }, /*#__PURE__*/React.createElement(BrandPreview, {
    logoImage: logoImage
  })));
}